import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

/**
 * Nombres de los miembros de la empresa, para "Generada por Juli" al lado de una creación
 * (spec 2026-09-11-invitaciones-equipo §6.2). `varios` es `false` con un solo miembro: ahí
 * la línea no se muestra, y para el 99 % de las cuentas ninguna pantalla cambia.
 *
 * La etiqueta vive en varias pantallas a la vez (Creaciones, la grilla inline, el panel de
 * Redes, la home, el menú del chat) y cada una montaba su propia consulta a `profiles`. La
 * caché de abajo es por empresa y a nivel módulo, con VENCIMIENTO: el equipo cambia desde
 * OTRO navegador (alguien acepta el link del dueño) y sin vencer el dueño seguía viendo un
 * solo miembro —y sin "Visibilidad"— hasta recargar (review atlas-code#176). Vence a los
 * dos minutos, los hooks montados vuelven a pedir al volver el foco a la pestaña, y
 * `olvidarNombresDelEquipo` (alta o baja en este navegador) avisa a los montados.
 */
type Mapa = Map<string, string>;

const VENCE_MS = 2 * 60 * 1000;

const cache = new Map<string, { promesa: Promise<Mapa>; desde: number }>();
const suscriptos = new Set<() => void>();

function nombreVisible(p: { name: string | null; email: string | null }): string {
  return p.name?.trim() || (p.email ?? '').split('@')[0] || 'Alguien';
}

async function cargar(idCompany: string): Promise<Mapa> {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, name, email')
    .eq('id_company', idCompany);
  if (error) throw error;
  const m: Mapa = new Map();
  for (const p of (data ?? []) as { id: string; name: string | null; email: string | null }[]) {
    m.set(p.id, nombreVisible(p));
  }
  return m;
}

/** Los nombres de una empresa, compartidos entre todos los que los pidan. */
export function nombresDelEquipo(idCompany: string, ahora: number = Date.now()): Promise<Mapa> {
  const enCache = cache.get(idCompany);
  if (enCache && ahora - enCache.desde < VENCE_MS) return enCache.promesa;
  // Un fallo no se cachea: el próximo que pregunte vuelve a intentar.
  const promesa = cargar(idCompany).catch((err) => {
    if (cache.get(idCompany)?.promesa === promesa) cache.delete(idCompany);
    throw err;
  });
  cache.set(idCompany, { promesa, desde: ahora });
  return promesa;
}

/** Para después de un alta o una baja en el equipo: los hooks montados vuelven a pedirlos. */
export function olvidarNombresDelEquipo(idCompany?: string): void {
  if (idCompany) cache.delete(idCompany);
  else cache.clear();
  for (const avisar of suscriptos) avisar();
}

export function useNombresDelEquipo(idCompany: string | null): {
  varios: boolean;
  nombreDe: (userId: string | null | undefined) => string | null;
} {
  const [mapa, setMapa] = useState<Mapa>(new Map());
  const [vuelta, setVuelta] = useState(0);
  useEffect(() => {
    const otraVez = () => setVuelta((n) => n + 1);
    suscriptos.add(otraVez);
    // Al volver a la pestaña se vuelve a preguntar; la caché decide si hace falta ir a la base.
    window.addEventListener('focus', otraVez);
    return () => {
      suscriptos.delete(otraVez);
      window.removeEventListener('focus', otraVez);
    };
  }, []);
  useEffect(() => {
    if (!idCompany) {
      setMapa(new Map());
      return;
    }
    let activo = true;
    nombresDelEquipo(idCompany)
      .then((m) => {
        if (activo) setMapa(m);
      })
      // Sin nombres no hay etiqueta: la pantalla queda como la de una cuenta sin equipo.
      .catch(() => {});
    return () => {
      activo = false;
    };
  }, [idCompany, vuelta]);
  return {
    varios: mapa.size > 1,
    nombreDe: (userId) => (userId ? (mapa.get(userId) ?? null) : null),
  };
}

export default useNombresDelEquipo;
